import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NuxxumLogo } from "@/components/NuxxumLogo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Module-level dedupe: React 18 StrictMode remounts this component in dev, which
// would otherwise cause the single-use session_id to be redeemed twice (2nd call
// returns 401, wiping the successful login). We share one in-flight promise per
// session_id across mounts so both effects await the same result.
const inFlight = new Map(); // sessionId -> Promise
const processedIds = new Set(); // sessionIds already successfully redeemed

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Retries on network errors (no response) — e.g. the preview/prod backend is
// waking up right when the user returns from Google. Never retries on 4xx,
// because the single-use session_id is already consumed at that point.
async function postWithRetry(sessionId) {
  let lastErr;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const res = await api.post("/auth/google/session", { session_id: sessionId });
      return res.data;
    } catch (err) {
      lastErr = err;
      if (err?.response) throw err;
      await sleep(2000 * (attempt + 1));
    }
  }
  throw lastErr;
}

function redeem(sessionId) {
  if (processedIds.has(sessionId)) return Promise.reject(new Error("already_processed"));
  let promise = inFlight.get(sessionId);
  if (!promise) {
    promise = postWithRetry(sessionId)
      .then((data) => {
        processedIds.add(sessionId);
        return data;
      })
      .finally(() => inFlight.delete(sessionId));
    inFlight.set(sessionId, promise);
  }
  return promise;
}

export default function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    if (!sessionId) {
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/auth", { replace: true });
      return;
    }

    redeem(sessionId)
      .then((data) => {
        if (cancelled) return;
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      })
      .catch(async (err) => {
        if (cancelled) return;
        // "already_processed" means a previous (successful) mount already handled it;
        // just wait for that mount's setUser+navigate to take effect — do nothing here.
        if (err?.message === "already_processed") return;
        // If the session_id was already consumed (e.g. user hit F5 on the callback URL),
        // maybe the cookies are still valid — try /auth/me before giving up.
        try {
          const { data } = await api.get("/auth/me");
          if (cancelled) return;
          setUser(data);
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/", { replace: true });
          return;
        } catch {
          // ignore, fall through
        }
        if (cancelled) return;
        window.history.replaceState(null, "", window.location.pathname);
        const detail = err?.response?.data?.detail;
        navigate("/auth", {
          replace: true,
          state: { googleError: typeof detail === "string" ? detail : "No se pudo completar el inicio de sesión con Google. Inténtalo de nuevo." },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#171927] flex items-center justify-center" data-testid="auth-callback-loading">
      <NuxxumLogo size={56} className="animate-pulse" />
    </div>
  );
}
